import React from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../App";
import ViewUsersModal from "./ViewUsersModal";

const StreakRing = ({ value, max, label }) => {
    const r = 28;
    const circ = 2 * Math.PI * r;
    const fill = Math.min(value / max, 1) * circ;
    return (
        <div className="relative w-14 h-14 flex-shrink-0">
            <svg viewBox="0 0 70 70" className="w-full h-full -rotate-90">
                <circle cx="35" cy="35" r={r} fill="none" stroke="#fed7aa" strokeWidth="6" className="dark:opacity-30" />
                <circle
                    cx="35" cy="35" r={r} fill="none"
                    stroke="#f97316" strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${fill} ${circ - fill}`}
                    style={{ transition: 'stroke-dasharray 0.7s ease' }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0">
                <span className="text-xs font-extrabold text-gray-900 dark:text-white leading-none">{value}</span>
                {label && <span className="text-[7px] text-gray-400 leading-none mt-0.5">{label}</span>}
            </div>
        </div>
    );
};

const MyProgress = ({ currentUser }) => {
    const navigate = useNavigate();
    const isPro = currentUser?.role === "Host";
    const [showFriends, setShowFriends] = React.useState(false);
    const [showMembers, setShowMembers] = React.useState(false);

    const streak = currentUser?.communityStreak || 0;
    const earnings = currentUser?.monthlySales || 0;
    const currency = currentUser?.country === "South Africa" ? "R" : "$";
    const activeDays = currentUser?.activeDays || 0;
    const highMatch = currentUser?.highMatchJobs || 0;
    const friends = currentUser?.friendsMade || 0;
    const members = currentUser?.membersJoined || 0;


    return (
        <div className="mb-3 md:mb-6">

            {/* ── Hero stat cards ── */}
            <div className="grid grid-cols-2 gap-3 mb-3">

                {/* Streak Card */}
                {(() => {
                    const milestones = [7, 14, 30, 60, 100];
                    const nextMilestone = milestones.find(m => streak < m) || 100;
                    const prevMilestone = milestones[milestones.indexOf(nextMilestone) - 1] || 0;
                    const progressToNext = nextMilestone - prevMilestone;
                    const progressDone = streak - prevMilestone;
                    const pct = Math.min((progressDone / progressToNext) * 100, 100);
                    return (
                        <div className={`relative flex flex-col justify-between gap-3 p-3 md:p-4 rounded-2xl border bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-800/10 border-orange-200 dark:border-orange-800/40 overflow-hidden${streak >= 7 ? " ring-1 ring-orange-400/50" : ""}`}>
                            {/* Top: ring + headline */}
                            <div className="flex items-start gap-3">
                                <StreakRing value={streak} max={nextMilestone} label="streak" />
                                <div className="flex-1 min-w-0 pt-0.5">
                                    <p className="text-[10px] text-orange-600 dark:text-orange-400 font-semibold uppercase tracking-wide leading-none">Day Streak</p>
                                    <p className="text-xl font-extrabold text-gray-900 dark:text-white mt-0.5 leading-none">
                                        {streak}
                                        <span className="text-xs font-normal text-gray-400 ml-1">day{streak !== 1 ? "s" : ""}</span>
                                    </p>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                                        {streak === 0
                                            ? "Post today to start! 💪"
                                            : streak >= 100
                                            ? "Legendary! 🏆"
                                            : streak >= 30
                                            ? "On fire! 🔥"
                                            : `${nextMilestone - streak} day${nextMilestone - streak !== 1 ? "s" : ""} to ${nextMilestone}-day badge`}
                                    </p>
                                </div>
                                {streak >= 7 && (
                                    <span className="text-base animate-bounce select-none flex-shrink-0">🔥</span>
                                )}
                            </div>
                            {/* Milestone progress bar */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[9px] text-orange-400 dark:text-orange-500 uppercase tracking-wide">Next: {nextMilestone}-day badge</span>
                                    <span className="text-[9px] text-orange-500 dark:text-orange-400 font-bold">{streak}/{nextMilestone}</span>
                                </div>
                                <div className="w-full h-2 rounded-full bg-orange-200/50 dark:bg-orange-800/30">
                                    <div
                                        className="h-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-700"
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Earnings Card */}
                {isPro ? (
                    <button
                        onClick={() => navigate(`${ROUTES.MY_PRODUCTS}?tab=payouts`)}
                        className="flex flex-col justify-between gap-3 p-3 md:p-4 rounded-2xl border bg-gradient-to-br from-teal-50 to-[#EAF4F8] dark:from-[#1F2E33] dark:to-[#1a2830] border-[#327691]/30 dark:border-[#327691]/40 text-left hover:shadow-md active:scale-[0.97] transition-all duration-200 w-full"
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#327691]/10 dark:bg-[#327691]/20 flex items-center justify-center">
                                <span className="text-lg select-none">💰</span>
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] text-[#327691] font-semibold uppercase tracking-wide leading-none">This Month</p>
                                <p className="text-sm font-extrabold text-gray-900 dark:text-white mt-1 leading-none">
                                    {currency}{earnings.toFixed(2)}
                                </p>
                                <p className="text-[10px] text-gray-400 mt-0.5">earned</p>
                            </div>
                        </div>
                        {/* Earnings progress bar — goal: 1000 */}
                        <div>
                            <div className="flex justify-between text-[9px] text-gray-400 mb-1">
                                <span>Goal: {currency}1,000</span>
                                <span>{Math.min(Math.round((earnings / 1000) * 100), 100)}%</span>
                            </div>
                            <div className="w-full h-1.5 rounded-full bg-[#327691]/10 dark:bg-[#327691]/20">
                                <div
                                    className="h-1.5 rounded-full bg-[#327691] transition-all duration-700"
                                    style={{ width: `${Math.min((earnings / 1000) * 100, 100)}%` }}
                                />
                            </div>
                        </div>
                    </button>
                ) : (
                    <button
                        onClick={() => navigate(ROUTES.SUBSCRIPTION_PLAN)}
                        className="relative flex flex-col justify-between gap-3 p-3 md:p-4 rounded-2xl border bg-gradient-to-br from-teal-50 to-[#EAF4F8] dark:from-[#1F2E33] dark:to-[#1a2830] border-[#327691]/30 dark:border-[#327691]/40 overflow-hidden text-left w-full"
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#327691]/10 dark:bg-[#327691]/20 flex items-center justify-center">
                                <span className="text-lg select-none">�</span>
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] text-[#327691] font-semibold uppercase tracking-wide leading-none">Earnings</p>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">Unlock with Pro</p>
                            </div>
                        </div>
                        <div className="absolute inset-0 bg-white/60 dark:bg-black/40 backdrop-blur-[2px] flex items-center justify-center rounded-2xl">
                            <span className="bg-[#327691] text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-sm">Upgrade</span>
                        </div>
                    </button>
                )}
            </div>

            {/* ── Sub-stat pills ── */}
            <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-[#2A2A2A] rounded-full px-3 py-1.5">
                    <span className="text-xs select-none">📅</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{activeDays}/7</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">days active</span>
                </div>
                <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-[#2A2A2A] rounded-full px-3 py-1.5">
                    <span className="text-xs select-none">🚀</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{highMatch}</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">job matches</span>
                </div>
                <button
                    onClick={() => setShowFriends(true)}
                    className="flex items-center gap-1.5 bg-gray-100 dark:bg-[#2A2A2A] rounded-full px-3 py-1.5"
                >
                    <span className="text-xs select-none">👤</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{friends}</span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">friends</span>
                </button>
                {isPro && (
                    <button
                        onClick={() => setShowMembers(true)}
                        className="flex items-center gap-1.5 bg-gray-100 dark:bg-[#2A2A2A] rounded-full px-3 py-1.5"
                    >
                        <span className="text-xs select-none">⭐</span>
                        <span className="text-xs font-bold text-gray-900 dark:text-white">{members}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">members</span>
                    </button>
                )}
            </div>

            <ViewUsersModal
                isOpen={showFriends}
                onClose={() => setShowFriends(false)}
                users={currentUser?.friendsMadeList || []}
                type="friends"
            />
            <ViewUsersModal
                isOpen={showMembers}
                onClose={() => setShowMembers(false)}
                users={currentUser?.membersJoinedList || []}
                type="members"
            />
        </div>
    );
};

export default MyProgress;